import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Category } from "@insite/client-framework/Services/CategoryService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import Translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonProps } from "@insite/mobius/Button";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FitImage from "@ucs/Components/Common/FitImage";
import { ApplicationCategories, CategoriesLvl1, getChildCategories } from "@ucs/Helpers/CategoryHelpers";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    description = "description",

    interiorsHideTab = "interiorsHideTab",
    interiorsTabTitleOverride = "interiorsTabTitleOverride",
    interiorsTabSeeAllLinkOverride = "interiorsTabSeeAllLinkOverride",
    interiorsImage = "interiorsImage",
    interiorsTitle = "interiorsTitle",
    interiorsSubtitle = "interiorsSubtitle",
    interiorsCtaLink = "interiorsCtaLink",
    interiorsProductLink = "interiorsProductLink",

    exteriorsHideTab = "exteriorsHideTab",
    exteriorsTabTitleOverride = "exteriorsTabTitleOverride",
    exteriorsTabSeeAllLinkOverride = "exteriorsTabSeeAllLinkOverride",
    exteriorsImage = "exteriorsImage",
    exteriorsTitle = "exteriorsTitle",
    exteriorsSubtitle = "exteriorsSubtitle",
    exteriorsCtaLink = "exteriorsCtaLink",
    exteriorsProductLink = "exteriorsProductLink",

    outdoorHideTab = "outdoorHideTab",
    outdoorTabTitleOverride = "outdoorTabTitleOverride",
    outdoorTabSeeAllLinkOverride = "outdoorTabSeeAllLinkOverride",
    outdoorImage = "outdoorImage",
    outdoorTitle = "outdoorTitle",
    outdoorSubtitle = "outdoorSubtitle",
    outdoorCtaLink = "outdoorCtaLink",
    outdoorProductLink = "outdoorProductLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;

        [fields.interiorsHideTab]: boolean;
        [fields.interiorsTabTitleOverride]: string;
        [fields.interiorsTabSeeAllLinkOverride]: LinkFieldValue;
        [fields.interiorsImage]: string;
        [fields.interiorsTitle]: string;
        [fields.interiorsSubtitle]: string;
        [fields.interiorsCtaLink]: LinkFieldValue;
        [fields.interiorsProductLink]: LinkFieldValue;

        [fields.exteriorsHideTab]: boolean;
        [fields.exteriorsTabTitleOverride]: string;
        [fields.exteriorsTabSeeAllLinkOverride]: LinkFieldValue;
        [fields.exteriorsImage]: string;
        [fields.exteriorsTitle]: string;
        [fields.exteriorsSubtitle]: string;
        [fields.exteriorsCtaLink]: LinkFieldValue;
        [fields.exteriorsProductLink]: LinkFieldValue;

        [fields.outdoorHideTab]: boolean;
        [fields.outdoorTabTitleOverride]: string;
        [fields.outdoorTabSeeAllLinkOverride]: LinkFieldValue;
        [fields.outdoorImage]: string;
        [fields.outdoorTitle]: string;
        [fields.outdoorSubtitle]: string;
        [fields.outdoorCtaLink]: LinkFieldValue;
        [fields.outdoorProductLink]: LinkFieldValue;
    };
    extendedStyles?: ApplicationsStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const categoriesList = getCategoriesDataView(state);
    return {
        categoriesList,
    };
};

const mapDispatchToProps = {
    loadCategories,
};

export interface ApplicationsStyles {
    wrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    title?: TypographyProps;
    description?: TypographyProps;
    categoryButtonsWrapper?: InjectableCss;
    categoryButtonGroup?: InjectableCss;
    lightButton?: ButtonProps;
    lightButtonActive?: ButtonProps;
    lightButtonOutlined?: ButtonProps;
    showcaseWrapper?: InjectableCss;
    showcaseTitle?: TypographyProps;
    showcaseSubtitle?: TypographyProps;
}

export const applicationsStyles: ApplicationsStyles = {
    wrapper: {
        css: css`
            position: relative;
            width: 100%;
            height: 86vh;
            max-height: 700px;
            display: flex;
            color: ${UCSColors.gray10};
            background-color: ${UCSColors.gray01};
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        height: 580px;
                    `,
                    css`
                        height: 580px;
                    `,
                ]);
            }}
        `,
    },
    contentWrapper: {
        css: css`        
            background: linear-gradient(0deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.65) 100%);
            position: absolute;
            width: 100%;
            height: 100%;
            padding: 64px 45px 20px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 32px 20px;
                    `,
                    css`
                        padding: 32px 20px;
                    `,
                ]);
            }}
        `,
    },
    title: {
        as: "h1",
        weight: 400,
        size: 64,
        css: css`
            letter-spacing: -0.05em;
            line-height: 72px;
            text-shadow: 0px 2px 20px rgba(0, 0, 0, 0.5);
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 36px;
                        line-height: 36px;
                        letter-spacing: -0.02em;
                    `,
                    css`
                        font-size: 36px;
                        line-height: 36px;
                        letter-spacing: -0.02em;
                    `,
                ]);
            }}
        `,
    },
    description: {
        as: "p",
        weight: 400,
        size: 22,
        css: css`
            max-width: 50%;
            margin-top: 11px;
            margin-bottom: 32px;
            line-height: 24px;
            text-shadow: 0px 2px 10px rgba(0, 0, 0, 0.5);
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        max-width: 100%;
                        margin-top: 8px;
                        margin-bottom: 16px;
                        font-size: 20px;
                        line-height: 24px;
                        letter-spacing: normal;
                    `,
                    css`
                        max-width: 90%;
                        margin-top: 8px;
                        margin-bottom: 16px;
                        font-size: 20px;
                        line-height: 24px;
                        letter-spacing: normal;
                    `,
                    css`
                        max-width: 75%;
                    `,
                    css`
                        max-width: 62%;
                    `,
                ]);
            }}
        `,
    },
    categoryButtonsWrapper: {
        css: css`
            display: flex;
            gap: 2px;
        `,
    },
    categoryButtonGroup: {
        css: css`
            width: fit-content;
            display: flex;
            flex-direction: column;
            gap: 2px;
        `,
    },
    lightButton: {
        hoverStyle: { backgroundColor: "rgba(255, 255, 255, 0.10)" },
        activeStyle: { backgroundColor: "rgba(255, 255, 255, 0.15)" },
        typographyProps: {
            size: 13,
            weight: 800,
            css: css`
                letter-spacing: 0.1em;
            `,
        },
        css: css`
            flex: 1;
            min-height: 36px;
            text-align: left;
            background-color: rgba(255, 255, 255, 0.25);
            padding: 10px 20px;
            border: none;
            transition: all 0.8s ease-in-out;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 10px;
                    `,
                    css`
                        padding: 10px;
                    `,
                ]);
            }}
        `,
    },
    lightButtonActive: {
        hoverStyle: { backgroundColor: "rgba(255, 255, 255, 0.80)" },
        activeStyle: { backgroundColor: "rgba(255, 255, 255, 0.85)" },
        typographyProps: {
            size: 13,
            weight: 800,
            css: css`
                letter-spacing: 0.1em;
            `,
        },
        css: css`
            flex: 1;
            min-height: 36px;
            text-align: left;
            color: ${UCSColors.gray00};
            background-color: ${UCSColors.gray10};
            padding: 10px 20px;
            border: none;
            transition: all 0.8s ease-in-out;
            cursor: default;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 10px;
                    `,
                    css`
                        padding: 10px;
                    `,
                ]);
            }}
        `,
    },
    lightButtonOutlined: {
        hoverStyle: { backgroundColor: "rgba(0, 0, 0, 0.17)", border: "2px solid rgba(255, 255, 255, 0.60)" },
        activeStyle: { backgroundColor: "rgba(0, 0, 0, 0.14)", border: "2px solid rgba(255, 255, 255, 0.55)" },
        typographyProps: {
            size: 13,
            weight: 500,
        },
        css: css`
            min-height: 25px;
            color: ${UCSColors.gray10};
            background-color: rgba(0, 0, 0, 0.1);
            padding: 5px 10px;
            border: 2px solid rgba(255, 255, 255, 0.5);
        `,
    },
    showcaseWrapper: {
        css: css`
            position: absolute;
            bottom: 20px;
            left: 45px;
            width: fit-content;
            min-width: 380px;
            max-width: 50%;
            background-color: rgba(0, 0, 0, 0.85);
            padding: 16px 24px 30px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        left: 20px;
                        padding: 16px 20px 24px;
                        min-width: 320px;
                        width: calc(100% - 40px);
                        max-width: calc(100% - 40px);
                    `,
                    css`
                        left: 20px;
                        padding: 16px 20px 24px;
                        min-width: 340px;
                        max-width: 94%;
                    `,
                    css`
                        max-width: 65%;
                    `,
                    css`
                        max-width: 58%;
                    `,
                ]);
            }}
        `,
    },
    showcaseTitle: {
        as: "h2",
        weight: 400,
        size: 18,
        css: css`
            letter-spacing: -0.02em;
            line-height: 24px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 16px;
                    `,
                    css`
                        font-size: 16px;
                    `,
                ]);
            }}
        `,
    },
    showcaseSubtitle: {
        as: "p",
        weight: 400,
        size: 14,
        css: css`
            line-height: 18px;
            margin-top: 4px;
            margin-bottom: 21px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 13px;
                        margin-bottom: 16px;
                    `,
                    css`
                        font-size: 13px;
                        margin-bottom: 16px;
                    `,
                ]);
            }}
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const HomeApplications: React.FC<Props> = ({ fields, extendedStyles, categoriesList, loadCategories, history, ...props }) => {
    const interiorsCtaLink = useGetLink(fields.interiorsCtaLink);
    const interiorsProductLink = useGetLink(fields.interiorsProductLink);
    const interiorsTabSeeAllLink = useGetLink(fields.interiorsTabSeeAllLinkOverride);
    const exteriorsCtaLink = useGetLink(fields.exteriorsCtaLink);
    const exteriorsProductLink = useGetLink(fields.exteriorsProductLink);
    const exteriorsTabSeeAllLink = useGetLink(fields.exteriorsTabSeeAllLinkOverride);
    const outdoorCtaLink = useGetLink(fields.outdoorCtaLink);
    const outdoorProductLink = useGetLink(fields.outdoorProductLink);
    const outdoorTabSeeAllLink = useGetLink(fields.outdoorTabSeeAllLinkOverride);

    const staticCategories: any[] = [{ name: "Interiors" }, { name: "Exteriors" }, { name: "Building Envelope" }];

    const [styles] = useState(() => mergeToNew(applicationsStyles, extendedStyles));
    const [timer, setTimer] = useState(5000);
    const [stopSlideshow, setStopSlideshow] = useState(false);
    const [activeCategory, setActiveCategory] = useState(() => {
        if (!fields.interiorsHideTab) return ApplicationCategories.Interiors;
        if (!fields.exteriorsHideTab) return ApplicationCategories.Exteriors;
        if (!fields.outdoorHideTab) return ApplicationCategories.BuildingEnvelope;
        return ApplicationCategories.Interiors;
    });    

    const filterCategories = (list: Category[]) => {
        if (list.length > 0) {
            const parentCategory = list.find(cat => cat.name === CategoriesLvl1.APPLICATION);
            const childCategories = getChildCategories(list, parentCategory?.subCategoryIds ?? []);
            return childCategories.length > 0 ? childCategories : staticCategories;
        }
        return staticCategories;
    };

    const [categories, setCategories] = useState(filterCategories(categoriesList.value || []));

    useEffect(() => {
        if (!categoriesList.isLoading && !categoriesList.value) {
            loadCategories();
        }
    }, []);

    useEffect(() => {
        categoriesList.value && setCategories(filterCategories(categoriesList.value));
    }, [categoriesList]);

    useEffect(() => {
        const id = setInterval(() => {
            if (stopSlideshow) {
                setTimer(999999);
            } else {
                setActiveCategory(nextCategory());
            }
        }, timer);
        return () => clearInterval(id);
    });

    const nextCategory = () => {
        const order = [
            ApplicationCategories.Interiors,
            ApplicationCategories.Exteriors,
            ApplicationCategories.BuildingEnvelope,
        ];
        const currentIndex = order.indexOf(activeCategory);
        for (let i = 1; i <= order.length; i++) {
            const candidate = order[(currentIndex + i) % order.length];
            if (!isTabHidden(candidate)) {
                return candidate;
            }
        }
        return activeCategory;
    };

    const getCategoryEnum = (name: string) => {
        switch (name) {
            case ApplicationCategories.Interiors:
                return ApplicationCategories.Interiors;
            case ApplicationCategories.Exteriors:
                return ApplicationCategories.Exteriors;
            case ApplicationCategories.BuildingEnvelope:
                return ApplicationCategories.BuildingEnvelope;

            default:
                return ApplicationCategories.Interiors;
        }
    };

    const getBackgroundImage = (name: string): string => {
        switch (name) {
            case ApplicationCategories.Interiors:
                return fields.interiorsImage;
            case ApplicationCategories.Exteriors:
                return fields.exteriorsImage;
            case ApplicationCategories.BuildingEnvelope:
                return fields.outdoorImage;

            default:
                return fields.interiorsImage;
        }
    };

    const getShowcaseTitle = (name: string): string => {
        switch (name) {
            case ApplicationCategories.Interiors:
                return fields.interiorsTitle;
            case ApplicationCategories.Exteriors:
                return fields.exteriorsTitle;
            case ApplicationCategories.BuildingEnvelope:
                return fields.outdoorTitle;

            default:
                return fields.interiorsTitle;
        }
    };

    const getShowcaseSubtitle = (name: string): string => {
        switch (name) {
            case ApplicationCategories.Interiors:
                return fields.interiorsSubtitle;
            case ApplicationCategories.Exteriors:
                return fields.exteriorsSubtitle;
            case ApplicationCategories.BuildingEnvelope:
                return fields.outdoorSubtitle;

            default:
                return fields.interiorsSubtitle;
        }
    };

    const getShowcaseCtaLink = (name: string) => {
        switch (name) {
            case ApplicationCategories.Interiors:
                return interiorsCtaLink.url;
            case ApplicationCategories.Exteriors:
                return exteriorsCtaLink.url;
            case ApplicationCategories.BuildingEnvelope:
                return outdoorCtaLink.url;

            default:
                return interiorsCtaLink.url;
        }
    };

    const getShowcaseProductLink = (name: string) => {
        switch (name) {
            case ApplicationCategories.Interiors:
                return interiorsProductLink.url;
            case ApplicationCategories.Exteriors:
                return exteriorsProductLink.url;
            case ApplicationCategories.BuildingEnvelope:
                return outdoorProductLink.url;

            default:
                return interiorsProductLink.url;
        }
    };

    const isTabHidden = (name: string): boolean => {
        switch (name) {
            case ApplicationCategories.Interiors:
                return fields.interiorsHideTab;
            case ApplicationCategories.Exteriors:
                return fields.exteriorsHideTab;
            case ApplicationCategories.BuildingEnvelope:
                return fields.outdoorHideTab;
            default:
                return false;
        }
    };

    const getTabTitleOverride = (name: string): string => {
        switch (name) {
            case ApplicationCategories.Interiors:
                return fields.interiorsTabTitleOverride;
            case ApplicationCategories.Exteriors:
                return fields.exteriorsTabTitleOverride;
            case ApplicationCategories.BuildingEnvelope:
                return fields.outdoorTabTitleOverride;
            default:
                return "";
        }
    };

    const getTabSeeAllLinkOverride = (name: string): string => {
        switch (name) {
            case ApplicationCategories.Interiors:
                return interiorsTabSeeAllLink.url || "";
            case ApplicationCategories.Exteriors:
                return exteriorsTabSeeAllLink.url || "";
            case ApplicationCategories.BuildingEnvelope:
                return outdoorTabSeeAllLink.url || "";
            default:
                return "";
        }
    };

    const setSelectedCategory = (categoryName: string) => {
        setStopSlideshow(true);
        setActiveCategory(getCategoryEnum(categoryName));
    };

    const navigateTo = (url: string) => {
        if (url.startsWith("http://") || url.startsWith("https://")) {
            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const getCategoryButtonStyles = (name: string) => {
        return activeCategory === name ? styles.lightButtonActive : styles.lightButton;
    };
    const imageContainerStyles = {
        css: css`
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            position: absolute;
            overflow: hidden;
            transition: opacity 1s ease-in-out;
        `,
    };
    return (
        <StyledWrapper {...styles.wrapper}>
            {categories &&
                categories.filter(category => !isTabHidden(category.name)).map(category => (
                    <FitImage 
                        key={category.name} 
                        image={getBackgroundImage(category.name)} 
                        focalPoint="center" 
                        imageExtendedStyles={{opacity: activeCategory === category.name ? 1 : 0}} 
                        containerStyles={imageContainerStyles}/>
                ))}
            <StyledWrapper {...styles.contentWrapper}>
                <Typography {...styles.title}>{fields.title}</Typography>
                <Typography {...styles.description}>{fields.description}</Typography>
                <StyledWrapper {...styles.categoryButtonsWrapper}>
                    {categories &&
                        categories.filter(category => !isTabHidden(category.name)).map(category => (
                            <StyledWrapper key={category.name} {...styles.categoryButtonGroup}>
                                <Button
                                    {...getCategoryButtonStyles(category.name)}
                                    onClick={() => setSelectedCategory(category.name)}
                                >
                                    {getTabTitleOverride(category.name) || category.name}
                                </Button>
                                <Button
                                    {...styles.lightButtonOutlined}
                                    onClick={() => {
                                        navigateTo(getTabSeeAllLinkOverride(category.name) || category.path);
                                    }}
                                    style={{
                                        width: "100%",
                                        opacity: activeCategory === category.name ? 1 : 0,
                                        visibility: activeCategory === category.name ? "visible" : "hidden",
                                        transition: "visibility 0.8s, opacity 0.8s ease-in-out",
                                    }}
                                >
                                    {Translate("See all")}
                                </Button>
                            </StyledWrapper>
                        ))}
                </StyledWrapper>
                {categories &&
                    categories.filter(category => !isTabHidden(category.name)).map(category => (
                        <StyledWrapper
                            key={category.name}
                            {...styles.showcaseWrapper}
                            style={{
                                opacity: activeCategory === category.name ? 1 : 0,
                                visibility: activeCategory === category.name ? "visible" : "hidden",
                                transition: "visibility 0.8s, opacity 0.8s ease-in-out",
                            }}
                        >
                            <Typography {...styles.showcaseTitle}>{getShowcaseTitle(category.name)}</Typography>
                            <Typography {...styles.showcaseSubtitle}>{getShowcaseSubtitle(category.name)}</Typography>
                            <Button
                                {...styles.lightButtonOutlined}
                                style={{ marginRight: 10 }}
                                onClick={() => {
                                    navigateTo(getShowcaseCtaLink(category.name) || "/");
                                }}
                            >
                                {Translate("See Project")}
                            </Button>
                            <Button
                                {...styles.lightButtonOutlined}
                                onClick={() => {
                                    navigateTo(getShowcaseProductLink(category.name) || "/");
                                }}
                            >
                                {Translate("Go to Product")}
                            </Button>
                        </StyledWrapper>
                    ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const tabGeneral = { displayName: "General", sortOrder: 0 };
const tabInteriors = { displayName: "Interiors", sortOrder: 1 };
const tabExteriors = { displayName: "Exteriors", sortOrder: 2 };
const tabOutdoor = { displayName: "Building Envelope", sortOrder: 3 };

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(HomeApplications)),
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tabGeneral,
                isRequired: true,
            },
            {
                name: fields.description,
                displayName: "Description Text",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tabGeneral,
                isRequired: true,
            },
            // Interiors
            {
                name: fields.interiorsHideTab,
                displayName: "Hide Tab?",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: tabInteriors,
            },
            {
                name: fields.interiorsTabTitleOverride,
                displayName: "Tab Title Override",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tabInteriors,
            },
            {
                name: fields.interiorsTabSeeAllLinkOverride,
                displayName: "Tab See All Link Override",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                tab: tabInteriors,
            },
            {
                name: fields.interiorsTitle,
                displayName: "Interiors Project Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tabInteriors,
                isRequired: true,
            },
            {
                name: fields.interiorsSubtitle,
                displayName: "Project Subtitle",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tabInteriors,
                isRequired: true,
            },
            {
                name: fields.interiorsImage,
                displayName: "Project Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                tab: tabInteriors,
                isRequired: true,
            },
            {
                name: fields.interiorsCtaLink,
                displayName: "CTA Project Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                tab: tabInteriors,
                isRequired: true,
            },
            {
                name: fields.interiorsProductLink,
                displayName: "CTA Product Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                tab: tabInteriors,
                isRequired: true,
            },
            // Exteriors
            {
                name: fields.exteriorsHideTab,
                displayName: "Hide Tab?",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: tabExteriors,
            },
            {
                name: fields.exteriorsTabTitleOverride,
                displayName: "Tab Title Override",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tabExteriors,
            },
            {
                name: fields.exteriorsTabSeeAllLinkOverride,
                displayName: "Tab See All Link Override",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                tab: tabExteriors,
            },
            {
                name: fields.exteriorsTitle,
                displayName: "Exteriors Project Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tabExteriors,
                isRequired: true,
            },
            {
                name: fields.exteriorsSubtitle,
                displayName: "Project Subtitle",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tabExteriors,
                isRequired: true,
            },
            {
                name: fields.exteriorsImage,
                displayName: "Project Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                tab: tabExteriors,
                isRequired: true,
            },
            {
                name: fields.exteriorsCtaLink,
                displayName: "CTA Project Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                tab: tabExteriors,
                isRequired: true,
            },
            {
                name: fields.exteriorsProductLink,
                displayName: "CTA Product Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                tab: tabExteriors,
                isRequired: true,
            },
            // Building Envelope
            {
                name: fields.outdoorHideTab,
                displayName: "Hide Tab?",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: tabOutdoor,
            },
            {
                name: fields.outdoorTabTitleOverride,
                displayName: "Tab Title Override",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tabOutdoor,
            },
            {
                name: fields.outdoorTabSeeAllLinkOverride,
                displayName: "Tab See All Link Override",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                tab: tabOutdoor,
            },
            {
                name: fields.outdoorTitle,
                displayName: "Building Envelope Project Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tabOutdoor,
                isRequired: true,
            },
            {
                name: fields.outdoorSubtitle,
                displayName: "Project Subtitle",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: tabOutdoor,
                isRequired: true,
            },
            {
                name: fields.outdoorImage,
                displayName: "Project Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                tab: tabOutdoor,
                isRequired: true,
            },
            {
                name: fields.outdoorCtaLink,
                displayName: "CTA Project Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                tab: tabOutdoor,
                isRequired: true,
            },
            {
                name: fields.outdoorProductLink,
                displayName: "CTA Product Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                tab: tabOutdoor,
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
